export type MediaQuery = 'mq1' | 'mq2' | 'mq3' | 'mq4' | 'mq5' | 'mq6';

export interface MediaQueryChangeEvent {
  current: MediaQuery;
  previous: MediaQuery;
}

export const MEDIA_QUERY_SIZES = {
  mq1: 320,
  mq2: 480,
  mq3: 768,
  mq4: 1024,
  mq5: 1280,
  mq6: 1440,
};

export const WB_RESIZE_EVENT_NAME = 'wbresize';

// NOTE: mq1 is implicit
const MEDIA_QUERIES_TO_OBSERVE: Array<MediaQuery> = ['mq2', 'mq3', 'mq4', 'mq5', 'mq6'];

// We are merging the declaration of the class with the interface to facilitate the reusability of the instances of the MQListeners
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging */

/**
 * @name MediaQueryService
 *
 * @description Class that informs about current media query
 *
 * @example
 * import { MediaQueryService } from '@workbench/core';
 *
 * const myMediaQueryService = new MediaQueryService(window);
 *
 * // To get the current media query
 * const currentMediaQuery: MediaQuery = myMediaQueryService.getCurrentMediaQuery();
 *
 * // To observe media query changes across time
 * window.addEventListener('wbresize', ((e: CustomEvent<MediaQueryChangeEvent>) => {
 *   console.log(`Media query changed from ${e.detail.previous} to ${e.detail.current}`);
 * }) as EventListener);
 *
 * // Pass in `true` if you are interested in the media query in the moment of calling the function
 * myMediaQueryService.observe(true);
 *
 * // When you don't need to observe anymore
 * myMediaQueryService.disconnect();
 */
export class MediaQueryService {
  /**
   * Map to store the MediaQueryService instance for each window
   * @internal
   */
  private static smap = new Map<Window, MediaQueryServiceInterface>();

  constructor(win: Window = window) {
    /// get or create the mediaQueryListener instance for the window
    let mediaQueryListener = MediaQueryService.smap.get(win);
    if (!mediaQueryListener) {
      mediaQueryListener = createMQListener(win);
      MediaQueryService.smap.set(win, mediaQueryListener);
    }

    /// add all methods from the interface to the instance
    Object.assign(this, mediaQueryListener);
  }
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- declaration merging with the class above
export interface MediaQueryService extends MediaQueryServiceInterface {}

interface MediaQueryServiceInterface {
  getCurrentMediaQuery(): MediaQuery;
  observe(emitCurrentValue?: boolean): void;
  disconnect(): void;
}

function createMQListener(win: Window): MediaQueryServiceInterface {
  const mqMap = MEDIA_QUERIES_TO_OBSERVE.map<[MediaQuery, MediaQueryList]>((mq) => [mq, win.matchMedia(`(min-width: ${MEDIA_QUERY_SIZES[mq]}px)`)]).reverse();

  let observingCount = 0;
  let previousMediaQuery: MediaQuery;
  let activeMediaQuery: MediaQuery;
  let raf: number | undefined;

  /**
   * Triggers a change event when the media query changes, to be used as a callback for the media query listeners
   * Uses requestAnimationFrame to avoid triggering the event multiple times when resizing the window from mq1 to mq6, for example
   * @internal
   */
  const triggerMQChange = () => (raf ??= requestAnimationFrame(updateMq));
  function updateMq() {
    raf = undefined; // reset raf
    previousMediaQuery = activeMediaQuery;
    activeMediaQuery = getCurrentMediaQuery();

    win.dispatchEvent(
      new CustomEvent<MediaQueryChangeEvent>(WB_RESIZE_EVENT_NAME, {
        detail: { current: activeMediaQuery, previous: previousMediaQuery },
      }),
    );
  }

  /**
   * returns the currently active media query
   */
  function getCurrentMediaQuery(): MediaQuery {
    for (const [mq, mediaQueryList] of mqMap) {
      if (mediaQueryList.matches) {
        return mq;
      }
    }

    // if no media query matches, return the first one
    return 'mq1';
  }

  /**
   * Starts observing all media query changes and dispatches `wbresize` events on `window`
   * If the observers are not already set.
   * @param emitCurrentValue set to true if you are interested in the media query in the moment of calling the function
   */
  function observe(emitCurrentValue: boolean = false): void {
    if (emitCurrentValue) {
      updateMq();
    }

    if (observingCount === 0) {
      for (const [, mediaQueryList] of mqMap) {
        mediaQueryList.addEventListener('change', triggerMQChange);
      }
    }

    observingCount++;
  }

  /**
   * Cleans up all remaining event listeners
   */
  function disconnect(): void {
    observingCount--;

    if (observingCount === 0) {
      for (const [, mediaQueryList] of mqMap) {
        mediaQueryList.removeEventListener('change', triggerMQChange);
      }
    }
  }

  return {
    getCurrentMediaQuery,
    observe,
    disconnect,
  };
}
